/**
 * /online — who has been around lately.
 *
 * The window is seven days, not "right now": see `routes/online.ts` for why a
 * literal presence light would be dark almost all of the time. The label on the
 * row says when, so nobody has to guess what the list means.
 *
 * Tapping a row goes to that person's profile, which is where the picks module
 * and the "challenge them" button live.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import type { OnlineUser } from '@shared/types'
import { teamName } from '@shared/teams'
import { useStore } from '../lib/store'
import { ApiFailure, GET } from '../lib/api'
import { Avatar, Body, Card, Header, Notice, Screen, SkeletonCard, Spacer, TeamLogo } from '../ui'

const DAY = 24 * 60 * 60 * 1000

function lastSeen(at: number, now: number): string {
  const age = now - at
  if (age < 5 * 60 * 1000) return 'just now'
  if (age < 60 * 60 * 1000) return `${Math.round(age / 60000)}m ago`
  if (age < DAY) return `${Math.round(age / 3600000)}h ago`
  const days = Math.round(age / DAY)
  return `${days}d ago`
}

export default function Online() {
  const { state } = useStore()
  const [users, setUsers] = useState<OnlineUser[] | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const now = Date.now()

  useEffect(() => {
    const ac = new AbortController()
    void (async () => {
      try {
        const res = await GET<{ users: OnlineUser[] }>('/api/online', ac.signal)
        setUsers(res.users)
        setErr(null)
      } catch (e) {
        if (ac.signal.aborted) return
        setErr(e instanceof ApiFailure ? e.message : 'Couldn’t load who’s around.')
      }
    })()
    return () => ac.abort()
  }, [])

  if (state?.user.optedOut) {
    return (
      <Screen style={{ flex: '1 1 auto', minHeight: 0 }}>
        <Header bordered={false} title={<span className="screen-title">👥 Online</span>} />
        <Body>
          <Card>
            <div style={{ fontSize: 15, fontWeight: 600 }}>You’ve opted out of the community.</div>
            <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 6, lineHeight: 1.5 }}>
              You’re hidden from this list, and it’s hidden from you. Both come back together.
            </div>
            <Link to="/settings" style={{ display: 'inline-block', marginTop: 12, fontSize: 13, fontWeight: 700 }}>
              Settings →
            </Link>
          </Card>
        </Body>
      </Screen>
    )
  }

  return (
    <Screen style={{ flex: '1 1 auto', minHeight: 0 }}>
      <Header
        bordered={false}
        title={<span className="screen-title">👥 Online</span>}
        right={
          users ? (
            <span className="mono" style={{ fontSize: 12, color: 'var(--dim)' }}>
              {users.length}
            </span>
          ) : undefined
        }
        sub={
          <div style={{ fontSize: 12, color: 'var(--dim)', marginTop: 4 }}>
            Anyone who has opened the app in the last week.
          </div>
        }
      />

      <Body style={{ padding: '8px var(--gutter) calc(24px + var(--nav-h))' }}>
        {err ? <><Notice tone="warn">{err}</Notice><Spacer h={14} /></> : null}

        {!users ? (
          <><SkeletonCard lines={3} /><Spacer h={12} /><SkeletonCard lines={3} /></>
        ) : users.length === 0 ? (
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 15, fontWeight: 600 }}>Quiet week.</div>
            <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 6 }}>
              Nobody has opened the app in the last seven days.
            </div>
          </div>
        ) : (
          users.map((u, i) => (
            <Link
              key={u.id}
              to={`/u/${encodeURIComponent(u.id)}`}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, minHeight: 60,
                padding: '12px 0', color: 'var(--fg)',
                borderTop: i === 0 ? undefined : '1px solid var(--line)',
              }}
            >
              <Avatar initials={u.initials} size={40} self={u.isViewer} />
              <div style={{ minWidth: 0, flex: '1 1 0%' }}>
                <div style={{ fontSize: 15, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {u.displayName}{u.isViewer ? ' (you)' : ''}
                  {u.isVerified ? <span aria-label="verified"> ✓</span> : null}
                </div>
                <div style={{ fontSize: 12, color: 'var(--dim)', marginTop: 2 }}>
                  {teamName(u.teamId)} · {lastSeen(u.lastSeenAt, now)}
                </div>
              </div>
              <TeamLogo teamId={u.teamId} size={28} fallback="none" />
            </Link>
          ))
        )}
      </Body>
    </Screen>
  )
}
